// Type imports
import type React from 'react'
import type { NextPage, GetStaticProps } from 'next'
import type { LinkTypes, I18nextKey, ActionTypes } from '../types'
import type { RequiredNs as ComponentNs } from '../components/localized/types'

// External imports
import Image from '../components/NextImage'
import { useRouter } from 'next/router'
import { NextSeo } from 'next-seo'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useTranslation, Trans } from 'react-i18next'

import styled from '@emotion/styled'

// Others
import useScrollPassed from '../hooks/use-scrolled-passed'

import Header from '../components/localized/Header'
import Footer from '../components/localized/Footer'
import PlatformLogos from '../components/localized/PlatformLogos'
import ChromeStoreRating from '../components/localized/ChromeStoreRating'
import Testimonials from '../components/localized/Testimonials'

import {
  FlexHeroSection,
  FlexItem as HeroFlexItem,
  Title as HeroTitle,
  Description as HeroDescription
} from '../components/Hero'
import { Section, FlexContainer, Item, FlexItem, Title, Description } from '../components/Section'
import { CenteredSection, Title as CenteredTitle } from '../components/CenteredSection'
import ColoredBackground from '../components/ColoredBackground'

import PrimaryAction from '../components/PrimaryAction'
import SecondaryAction from '../components/SecondaryAction'
import Highlight, { HighlightColor } from '../components/Highlight'
import FeatureGrid, { FeatureBlock } from '../components/FeatureGrid'
import BottomCta from '../components/BottomCta'

import StatisticsMenuImage from '../../public/assets/feature-images/en/statistics-menu.svg'

import { defaultPageSpecificSEO, MOBILE_WIDTH_PX, pageSpecificCtaLink } from '../constants'
/**
 * Load all the namespaces required for this page include all localized components
 */
type RequiredNs = 'readability' | 'common' | ComponentNs
const namespaces: Record<RequiredNs, true> = { common: true, readability: true }
export const getStaticProps: GetStaticProps = async ({ locale }) => ({
  props: {
    ...(await serverSideTranslations(locale ?? '', Object.keys(namespaces)))
  }
})

/**
 * Constants
 */

const HERO_BACKGROUND = 'green'

const MobileHiddenHeroFlexItem = styled(HeroFlexItem)`
  @media screen and (max-width: ${MOBILE_WIDTH_PX}px) {
    display: none;
  }
  align-items: center;
`

/**
 * Types for structure of page
 */

// did this manually for each key because it was a pain to sort out typescript issues
// but it would be better if we just type it so its just the prefix of the key needed
type ReadabilityKey = I18nextKey<typeof import ('../../public/locales/en/readability.json')>
interface FlexSectionContent extends Record<'titleKey' | 'descriptionKey', ReadabilityKey> {
  img: { src: any } & Record<'altKey', ReadabilityKey>
  action?: LinkTypes & Record<'textKey', ReadabilityKey>
  color?: HighlightColor
}

type FeatureBlockContent = (
  Record<'titleKey' | 'descriptionKey', ReadabilityKey> &
  Omit<FeatureBlock, 'title' | 'description'> &
  Partial<LinkTypes> // IDK why but typescript does not find the 'linkTypes' in FeatureBlock?
)

/**
 * Mapping json keys for content structure
 */

const FEATURES: FlexSectionContent[] = [
  {

    titleKey: 'features.readability.TITLE',
    descriptionKey: 'features.readability.DESCRIPTION',
    img: {
      src: require('../../public/assets/feature-images/en/readability.svg'),
      altKey: 'features.readability.STATS_IMG_ALT'
    },
    color: 'green'
  },
  {
    titleKey: 'features.improve.TITLE',
    descriptionKey: 'features.improve.DESCRIPTION',
    action: { href: '/pro', textKey: 'features.improve.ACTION_TEXT' },
    img: {
      src: require('../../public/assets/feature-images/en/style-various.svg'),
      altKey: 'features.improve.STYLE_VARIOUS_IMG_ALT'
    },
    color: 'blue'
  }
]

const ADV_FEATURES: FeatureBlockContent[] = [
  {
    titleKey: 'advfeatures.gradeLevel.TITLE',
    descriptionKey: 'advfeatures.gradeLevel.DESCRIPTION',
    icon: { src: require('../../public/assets/icons/bulb.svg'), alt: '' }
  },
  {
    titleKey: 'advfeatures.wordCount.TITLE',
    descriptionKey: 'advfeatures.wordCount.DESCRIPTION',
    icon: { src: require('../../public/assets/icons/trending-up.svg'), alt: '' }
  },
  {
    titleKey: 'advfeatures.readingTime.TITLE',
    descriptionKey: 'advfeatures.readingTime.DESCRIPTION',
    icon: { src: require('../../public/assets/icons/time.svg'), alt: '' }
  }
]

/**
 * Page Component
 */

const Readability: NextPage = () => {
  const router = useRouter()

  const { t } = useTranslation('readability')
  const { t: tCommon } = useTranslation('common')

  const ctaAction: ActionTypes = { ...t('CTA_BUTTON'), ...pageSpecificCtaLink(router.pathname, router.locale) }

  // Controls for the header to only go sticky after user passes the fold
  const { ref: heroCtaRef, scrolledPassed } = useScrollPassed()

  return (
    <>
      <NextSeo
        {...defaultPageSpecificSEO(router.pathname, router.locale)}
        {...t('SEO')}
      />

      {/* Above the fold */}
      <Header
        t={tCommon}
        action={ctaAction}
        data-sticky={scrolledPassed}
        bgColor={HERO_BACKGROUND}
        dark
      />
      <ColoredBackground bgColor={HERO_BACKGROUND}>
        {/* Hero */}
        <FlexHeroSection>
          <HeroFlexItem>
            <HeroTitle>{t('hero.TITLE')}</HeroTitle>
            <HeroDescription>{t('hero.DESCRIPTION')}</HeroDescription>
            <div>
              <PrimaryAction {...ctaAction} ref={heroCtaRef} clickId='hero-cta' />
              <ChromeStoreRating t={tCommon} />
            </div>
          </HeroFlexItem>
          <MobileHiddenHeroFlexItem>
            <Image src={StatisticsMenuImage} alt={t('hero.WRITING_STATISTICS_IMG_ALT')} width={384} height={469} priority />
          </MobileHiddenHeroFlexItem>
        </FlexHeroSection>
      </ColoredBackground>

      {/* Features */}
      <Section bgColor='gray'>
        {FEATURES.map(({ titleKey, descriptionKey, img, action, color }, i) => {
          return (
          <FlexContainer key={titleKey} direction={i % 2 ? 'row-reverse' : 'row'}>
            <Item>
              <Image src={img.src} alt={t(img.altKey) as string} height={345} width={483} />
            </Item>
            <FlexItem>
              <div>
                <Title>
                  <Trans
                    t={t}
                    i18nKey={titleKey}
                    components={[<Highlight key={i} color={color} />]}
                  />
                </Title>
                <Description>{t(descriptionKey)}</Description>
              </div>
              {action && <SecondaryAction {...action} text={t(action.textKey) as string} />}
            </FlexItem>
          </FlexContainer>
          )
        })}
      </Section>

      {/* Platforms */}
      <CenteredSection>
        <CenteredTitle>
          <Trans t={t} i18nKey='platforms.TITLE'><Highlight color='yellow'/></Trans>
        </CenteredTitle>
        <PlatformLogos t={tCommon} platforms={['google-docs', 'gmail', 'outlook', 'facebook', 'linkedin', 'wordpress']} />
      </CenteredSection>

      {/* Advanced features */}
      <CenteredSection bgColor='gray'>
        <CenteredTitle>
          <Trans t={t} i18nKey={'advfeatures.TITLE'}><Highlight color='yellow'/></Trans>
        </CenteredTitle>
        <FeatureGrid
          list={ADV_FEATURES.map(({ titleKey, descriptionKey, ...rest }) => ({
            ...rest,
            title: t(titleKey) as string,
            description: t(descriptionKey) as string
          }))}
        />
      </CenteredSection>

      {/* Testimonials */}
      <Testimonials t={tCommon} />

      {/* BottomCta */}
      <BottomCta action={ctaAction} {...t('BOTTOM_CTA')} />

      {/* Footer */}
      <Footer t={tCommon} />
    </>
  )
}

export default Readability
